import React, {useState, useEffect} from 'react';
import validator from 'validator';
import {useHistory} from 'react-router-dom';
import {
    useTheme,
    TextField,
    useMediaQuery,
    Dialog,
    Grid,
    Divider,
} from '@mui/material';

import {
    validateEmail,
    isThereFourConsecutiveNumber,
    allEqual,
    validateCouponDateTime,
    isAscendingDescending,
    randomNumber,
} from '@utils/utilityFunctions';
import {CustomButton} from '@buttons/CustomButton';
import {useDriverStore} from '@store/driverInfoStore';
import {createContact} from '@api/createContact';
import {useTokenStore} from '@store/TokenStore';
import {useCPStore} from '@store/cpStore';
import {useStyles} from '../Tab/useStyle';
import {useCarStore} from '@store/carInfoStore';
import {useSelectedQuoteStore} from '@store/QuoteStore';
import {useTrackingStore} from '@store/TrackingStore';
import {usePolicyStore} from '@store/policyStore';
import TagManager from 'react-gtm-module';
import {upCRM, upCRMRandom} from '@utils/CRM';
import {getTracking} from '@api/getTracking';
import {useCarListStore} from '@store/carListStore';
import {Warning, Check, Error, ArrowForward} from '@mui/icons-material';
import {getBasePrice} from '@api/getBasePrice';
import {getToken} from '@api/getToken';
import carRunning from '@assets/images/carRunning.json';
import Lottie from 'react-lottie';
import { checarProduct } from '@utils/product';
import { generateDate } from '@utils/generateDate';

import { datadogRum } from '@datadog/browser-rum';


interface FormInformationProps {
    setIsFormInformation: React.Dispatch<React.SetStateAction<boolean>>;
    isFormInformation: boolean;
}

export const FormInformation: React.FC<FormInformationProps> = ({
                                                                    setIsFormInformation,
                                                                    isFormInformation,
                                                                }) => {
    const {token} = useTokenStore();
    const {breakpoints} = useTheme();
    const isTablet = useMediaQuery(breakpoints.down('md'));
    const isMobile = useMediaQuery(breakpoints.down('sm'));
    const isDesktop = useMediaQuery(breakpoints.up('lg'));
    const {driver, setDriver} = useDriverStore();
    const {selectedQuote} = useSelectedQuoteStore();
    const {tracking, setTracking} = useTrackingStore();
    const {searchCP, cpData} = useCPStore();
    const [basePrice, setBasePrice] = useState<{ total: string }>({total: ''});
    const {car} = useCarStore();
    const {carList} = useCarListStore();
    const { startDate } = generateDate();
    const classes = useStyles({
        isMobile,
        isTablet,
        isDesktop,
    });
    const {policy, addPolicyInfo} = usePolicyStore();
    const [warningTelefono, setWarningTelefono] = useState<{
        msg: string;
        isWarning: boolean;
    }>({
        msg: '',
        isWarning: false,
    });
    const [errorTelefono, setErrorTelefono] = useState<{
        msg: string;
        isError: boolean;
        isCorrect: boolean;
    }>({
        msg: '',
        isError: false,
        isCorrect: false,
    });
    const [errorEmail, setErrorEmail] = useState<{
        msg: string;
        isError: boolean;
        isCorrect: boolean;
    }>({
        msg: '',
        isError: false,
        isCorrect: false,
    });

    const [driverPhone, setDriverPhone] = useState('');
    const [driverEmail, setDriverEmail] = useState('');
    const [device, setDevice] = useState('')
    const history = useHistory();

    function handleDeviceDetection() {
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(userAgent);
        const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

        if (isMobile || isTablet) {
            return '1'
        } else return '2'
    };

    React.useEffect(() => {
        setDevice(handleDeviceDetection())
        let basePrice;
        (async () => {
            basePrice = await getBasePrice(
                await getToken(),
                '33',
                car.description,
                car.model,
                driver.genero === 'Mujer' ? 'M' : 'H'
            );
            if (Object.keys(basePrice).some((key) => key === 'total')) {
                setBasePrice(basePrice);
            }
        })();
    }, []);

    const handleInputChange = (event: any) => {
        let regex = /^[^0][0-9]*$/;
        const telefono = event.target.value;
        if (event.target.id === 'telefono') {
            if (regex.test(telefono)) {
                if (telefono.length < 10 || telefono.length > 10) {
                    setErrorTelefono({
                        msg: '¡Oops! Soló puedes escribir 10 caracteres',
                        isError: true,
                        isCorrect: false,
                    });
                } else {
                    setDriverPhone(telefono);
                    setDriver({
                        ...driver,
                        telefono: telefono,
                    });
                    setErrorTelefono({
                        msg: '',
                        isError: false,
                        isCorrect: true,
                    });
                }
            } else {
                setErrorTelefono({
                    msg: '¡Oops! Aquí sólo puedes escribir números.',
                    isError: true,
                    isCorrect: false,
                });
                setWarningTelefono({
                    msg: '',
                    isWarning: false,
                });
            }
            const telefonoArray = telefono.split('');

            const isResultAllEquals = allEqual(telefonoArray);
            const isConsecutive = isThereFourConsecutiveNumber(telefono);
            const isAscDesc = isAscendingDescending(telefono);

            if (
                isResultAllEquals ||
                telefono === '0123456789' ||
                telefono.startsWith('00' || '000' || '0000') ||
                isConsecutive || isAscDesc
            ) {
                setErrorTelefono({
                    msg: '¡Oops! Debes ingresar un teléfono válido.',
                    isError: true,
                    isCorrect: false,
                });
                return;
            }
            return;
        }

        if (event.target.id === 'email') {
            const emailValidate =
                /^[a-zA-Z0-9._-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
            setDriverEmail(event.target.value);
            const email = event.target.value;
            // Expresión regular que verifica que no haya letras con acento en el correo electrónico
            const accentRegex = /[áéíóúüÁÉÍÓÚÜ]/;

            if (!emailValidate.test(email) || accentRegex.test(driverEmail)) {
                setErrorEmail({
                    msg: '¡Oh, no! El email  no es correcto. Chécalo para continuar.',
                    isError: true,
                    isCorrect: false,
                });
                return;
            }

            if (!validateEmail(email)) {
                setErrorEmail({
                    msg: '¡Oh, no! El email que ingresaste no es correcto. Chécalo para continuar.',
                    isError: true,
                    isCorrect: false,
                });
                return;
            }

            const [beforeAt, afterAt] = email.split('@');
            const blacklist = [
                'asd@',
                'lkj@',
                'qwe@',
                'poi@',
                'zxc@',
                'mnb@',
                'gmail.mx',
                'hotmail.mx',
                'aol',
                'hotmail.com.mx',
                'gmail.com.mx',
                'live.com.mx',
                'gmail.co',
                'ensamble.dev',
                'outloot.com',
                'yahoo.com.sg',
                '@',
                'yopmail.com',
                'hotmail.co',
            ];
            if (beforeAt.length <= 2 || blacklist.includes(afterAt)) {
                setErrorEmail({
                    msg: '¡Oh, no! Debes ingresar un correo válido.',
                    isError: true,
                    isCorrect: false,
                });
                return;
            }

            if (email === '') {
                setErrorEmail({
                    msg: '¡Oh, no! Te falta llenar este campo para poder continuar.',
                    isError: true,
                    isCorrect: false,
                });
                return;
            } else {
                setDriver({
                    ...driver,
                    email,
                });
                setErrorEmail({
                    msg: '',
                    isError: false,
                    isCorrect: true,
                });
            }
        }
    };

    const firstDataLayer = {
        event: 'CSform_step_2_view',
        eventCategory: 'Formulario.Seguro.Auto.v2',
        eventAction: 'step_2_view',
        eventLabel: undefined,
        telefono: undefined,
        correo: undefined,
        noCotizacion: policy.policyNumber,
        modelo: car.description,
        marca: car.assembler,
        anio: car.model,
        paquete: undefined,
        forma_pago: undefined,
        aseguradora: undefined,
        tarjeta: undefined,
        tipo_tarjeta: undefined,
        tipo_vehiculo: car.type,
        colaborador: undefined,
        cupon: undefined,
    };

    const tagManagerArgs = {
        dataLayer: firstDataLayer,
    };

    useEffect(() => {
        TagManager.dataLayer(tagManagerArgs);
        (async () => {
            await searchCP(driver.codigoPostal, token);
        })();
    }, []);

    const handleCreateDriver = async () => {
        if (tracking.id === undefined || tracking?.id === undefined) {
            setTracking(token);
        }

        let id = tracking.id;
        if ( randomNumber() === 10 && !checarProduct()) {
            let crm = {
            business: 'cocheseguro',
            business_key: 'MEZUBO',
            contact_now: false,
            night_promo: validateCouponDateTime(policy.endDateTime, policy.acceptCoupon),
            tracking_id: id,
            name: driver.name,
            cellphone: driver.telefono,
            last_name: '',
            second_last_name: '',
            phone: driver.telefono,
            sub_channel_sale: 'Inter.mx',
            email: driver.email,
            gender: driver.genero,
            birth_date: driver.fechaNacimiento,
            township_code: '',
            suburb_code: '',
            vehicle_id: car.vehicle_id,
            rfc: '',
            address: '',
            suburb_name: '',
            township_name: '',
            Tiporelacion: '',
            zip_code: driver.codigoPostal,
            insurance: '',
            campana: '',
            portal_origen: 'https://www.inter.mx',
            grupo_economico: 'Coche Seguro',
            url:
                'https://' +
                window.location.hostname +
                '/' +
                '?utm_source=' +
                window.localStorage.getItem('utm_source') +
                '&utm_medium=' +
                window.localStorage.getItem('utm_medium') +
                '&utm_campaign=' +
                window.localStorage.getItem('utm_campaign') +
                '&utm_content=' +
                window.localStorage.getItem('utm_content'),
            device: device,
            scoreLead: device,
        };
            let crmresponse = await createContact(token, crm);
            policy.policyNumber = crmresponse?.data?.Folio;
            policy.uuid = crmresponse?.data?.Segurify_uuid;
        } else {
            let crm = {
            business: 'cocheseguro',
            contact_now: false,
            night_promo: validateCouponDateTime(policy.endDateTime, policy.acceptCoupon),
            tracking_id: id,
            name: driver.name,
            cellphone: driver.telefono,
            last_name: '',
            second_last_name: '',
            phone: driver.telefono,
            sub_channel_sale: 'Inter.mx',
            email: driver.email,
            gender: driver.genero,
            birth_date: driver.fechaNacimiento,
            township_code: '',
            suburb_code: '',
            vehicle_id: car.vehicle_id,
            rfc: '',
            address: '',
            suburb_name: '',
            township_name: '',
            Tiporelacion: '',
            zip_code: driver.codigoPostal,
            insurance: '',
            campana: '',
            portal_origen: 'https://www.inter.mx',
            grupo_economico: 'Coche Seguro',
            url:
                'https://' +
                window.location.hostname +
                '/' +
                '?utm_source=' +
                window.localStorage.getItem('utm_source') +
                '&utm_medium=' +
                window.localStorage.getItem('utm_medium') +
                '&utm_campaign=' +
                window.localStorage.getItem('utm_campaign') +
                '&utm_content=' +
                window.localStorage.getItem('utm_content'),
            device: device,
            scoreLead: device,
        };
            let crmresponse = await createContact(token, crm);
            policy.policyNumber = crmresponse?.data?.Folio;
            policy.uuid = crmresponse?.data?.Segurify_uuid;
        }
        policy.stage = '1. Registro (Coche)';
        policy.sub_stage = '1. Datos Generales';
        policy.state_code = 'Borrador';
        policy.status_code = 'En revisión';
        policy.device = device;
        policy.scoreLead = device;
        policy.start_at = startDate;
        await addPolicyInfo({...policy});
        let initialData = {
            id: tracking.id,
            business: 'cocheseguro',
            data: {
                car: car,
                driver: driver,
                selectedQuote: selectedQuote,
                policy: policy,
                cpData: cpData,
                carList: carList,
            },
        };
        await getTracking(token, initialData);
        upCRM(token, car, driver, selectedQuote, policy, tracking, cpData);

        datadogRum.setUser({
            id: policy.uuid,
            uuid: policy.uuid,
            name: driver.name,
            email:  driver.email,
            vehicle_id: car.vehicle_id,
            zip_code: driver.codigoPostal,
            no_cotizacion: policy.policyNumber,
            modelo: car.description,
            marca: car.assembler,
            anio: car.model,
        });

        TagManager.dataLayer({
            dataLayer: {
                event: 'CSform_step_2_submit',
                eventCategoy: 'Formulario.Seguro.Auto.v2',
                eventAction: 'step_2_submit',
                eventLabel: undefined,
                telefono: driver.telefono,
                correo: driver.email,
                noCotizacion: policy.policyNumber,
                modelo: car.description,
                marca: car.assembler,
                anio: car.model,
                paquete: undefined,
                forma_pago: undefined,
                aseguradora: undefined,
                tarjeta: undefined,
                tipo_tarjeta: undefined,
                tipo_vehiculo: car.type,
                colaborador: undefined,
                cupon: undefined,
            },
        });

        history.push('/plans');
    };

    const colorTelefonoAdornment = () => {
        if (warningTelefono.isWarning) {
            return 'warning';
        }

        if (errorTelefono.isError) {
            return 'error';
        }

        return 'success';
    };

    const colorHelperTextTelefono = () => {
        if (warningTelefono.isWarning) {
            return '#FF9E1B';
        }

        if (errorTelefono.isError) {
            return '#f44336';
        }

        return '#5AB52F';
    };

    const telefonoAdornment = () => {
        if (warningTelefono.isWarning) {
            return <Warning color={'warning'}/>;
        }

        if (errorTelefono.isError) {
            return <Error color={'error'}/>;
        }
        if (errorTelefono.isCorrect) {
            return <Check color={'success'}/>;
        }

        return <></>;
    };

    const emailAdornment = () => {
        if (errorEmail.isError) {
            return <Error color={'error'}/>;
        }
        if (errorEmail.isCorrect) {
            return <Check color={'success'}/>;
        }

        return <></>;
    };

    return (
        <Dialog
            maxWidth={'xs'}
            open={isFormInformation}
            disableEscapeKeyDown
            onClose={(e, reason) => {
                if (reason !== 'backdropClick') {
                    setIsFormInformation(false);
                }
            }}
            scroll="body"
        >
            <Grid container padding={2} rowSpacing={2}>
                <Grid
                    item
                    container
                    rowSpacing={0}
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                >
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Lottie
                            options={{
                                animationData: carRunning,
                                loop: true,
                                autoplay: true,
                                rendererSettings: {
                                    preserveAspectRatio: 'xMidYMid slice',
                                },
                            }}
                        />
                    </Grid>
                    {basePrice.total && (
                        <>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <p
                                    className={classes.text}
                                    style={{
                                        textAlign: 'center',
                                        width: '100%',
                                        padding: 0,
                                        margin: 0,
                                        fontWeight: 400,
                                        fontSize: '18px',
                                        color: '#212121',
                                    }}
                                >
                                    Protege tu <strong>{car.description}</strong> con el mejor
                                    seguro desde:
                                </p>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <p
                                    className={classes.price}
                                    style={{padding: 0, margin: '8px 0'}}
                                >
                                    {Number(basePrice.total).toLocaleString('en', {
                                        style: 'currency',
                                        currency: 'USD',
                                    })}
                                </p>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <p
                                    className={classes.text}
                                    style={{
                                        textAlign: 'center',
                                        width: '100%',
                                        padding: 0,
                                        margin: 0,
                                        fontSize: '18px',
                                        fontWeight: 700,
                                        color: '#212121',
                                    }}
                                >
                                    Con un plan Básico
                                </p>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Divider variant="middle" component="hr"/>
                            </Grid>
                        </>
                    )}
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <p
                        className={classes.text}
                        style={{
                            textAlign: 'center',
                            fontSize: '16px',
                            fontWeight: 400,
                            color: '#212121',
                            margin: '0',
                        }}
                    >
                        ¡Déjanos tus datos para comparar hasta{' '}
                        <strong>9 distintas opciones de aseguradoras</strong> y ayudarte a
                        encontrar la mejor oferta!
                    </p>
                </Grid>
                {!basePrice.total && (
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Divider variant="middle" component="hr"/>
                    </Grid>
                )}
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <label className={classes.label}>¿Cuál es tu teléfono?</label>
                    <TextField
                        sx={{
                            marginTop: 1,
                            width: '100%',
                        }}
                        className={classes.phoneInput}
                        type="number"
                        name="telefono"
                        label="Telefono"
                        id="telefono"
                        variant="outlined"
                        placeholder="Telefono"
                        onChange={handleInputChange}
                        error={errorTelefono.isError}
                        onInput={(e: any) => {
                            e.target.value = Math.max(0, parseInt(e.target.value))
                                .toString()
                                .slice(0, 10);
                        }}
                        onBlur={(event) => {
                            setWarningTelefono({msg: '', isWarning: false});
                            const telefonoArray = event.target.value.split('');

                            const isResultAllEquals = allEqual(telefonoArray);
                            const isConsecutive = isThereFourConsecutiveNumber(
                                event.target.value
                            );
                            const isAscDesc = isAscendingDescending(event.target.value);

                            if (
                                event.target.value.length > 10 ||
                                event.target.value.length < 10
                            ) {
                                setErrorTelefono({
                                    msg: '¡Oops! El campo requerido es de 10 digitos.',
                                    isError: true,
                                    isCorrect: false,
                                });
                                return;
                            }

                            if (
                                isResultAllEquals ||
                                event.target.value === '0123456789' ||
                                event.target.value.startsWith('00' || '000' || '0000') ||
                                isConsecutive || isAscDesc
                            ) {
                                setErrorTelefono({
                                    msg: '¡Oops! Debes ingresar un teléfono válido.',
                                    isError: true,
                                    isCorrect: false,
                                });
                                return;
                            }
                            setErrorTelefono({
                                msg: '',
                                isError: false,
                                isCorrect: true,
                            });
                        }}
                        helperText={
                            <label style={{color: colorHelperTextTelefono()}}>
                                {errorTelefono.msg || warningTelefono.msg}
                            </label>
                        }
                        inputProps={{
                            maxLength: 10,
                            inputMode: 'tel',
                        }}
                        color={colorTelefonoAdornment()}
                        InputProps={{
                            style: {
                                borderRadius: 8,
                            },
                            endAdornment: telefonoAdornment(),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <label className={classes.label}>¿Y tu correo?</label>
                    <TextField
                        id="email"
                        label="Correo electrónico"
                        variant="outlined"
                        placeholder="Correo electrónico"
                        onChange={handleInputChange}
                        onBlur={() => {
                            // Expresión regular que verifica que no haya letras con acento en el correo electrónico
                            const accentRegex = /[áéíóúüÁÉÍÓÚÜ]/;

                            if (driverEmail === '' || accentRegex.test(driverEmail)) {
                                setErrorEmail({
                                    msg: '¡Oops! Debes ingresar un email válido.',
                                    isError: true,
                                    isCorrect: false,
                                });
                                return;
                            }
                        }}
                        error={errorEmail.isError}
                        helperText={errorEmail.msg}
                        sx={{
                            marginTop: 1,
                            width: '100%',
                        }}
                        color={validator.isEmail(driver.email) ? 'success' : 'primary'}
                        InputProps={{
                            style: {
                                borderRadius: 8,
                            },
                            endAdornment: emailAdornment(),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <CustomButton
                        color={'primary'}
                        disabled={
                            errorEmail.isError ||
                            errorTelefono.isError ||
                            !driverEmail ||
                            !driverPhone
                        }
                        onClick={handleCreateDriver}
                        variant={'contained'}
                        text={'¡Listo!'}
                        style={{
                            width: '100%',
                            height: '56px',
                            textTransform: 'none',
                        }}
                        endIcon={<ArrowForward/>}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
};
